
/**
 * @file barrel.js
 * @desc This module contains Barrel class definition.
 * @author Igor Kling <kling.igor.ksi@gmail.com>
 * @version 1.0.dev
 * @copyright 2016 (c) KSI Corporation
 * */

/**
 *
 * @requires {Reel}
 * */

(/** @lends <global> */function (global) {
    'use strict';

    var Reel = require('reel').Reel;


    /**
     * Constructs Barrel.
     * @class
     * @constructor
     * @param {Object} options Description object
     * @param {Number} symbolsCount Unique symbols number
     * @name Barrel
     * @classdesc This is complete barrel implementation sufficient for slot games.
     * @tutorial 1.3-adding-barrel
     * @tutorial 1.6-rotating-barrel
     * @tutorial 1.8-adding-barrel-sounds
     * */
    var Barrel = function (options, symbolsCount) {
        // this.container = new PIXI.particles.ParticleContainer(
        //     100,
        //     {
        //         rotation: false,
        //         alpha: true,
        //         scale: false,
        //         uvs: true
        //     }
        // );
        this._delegate = null;


        this.rows = options.rows;

        this.reelsContainer = new PIXI.Container();

        this.reelsContainer.interactive = true;
        this.reelsContainer.buttonMode = false;

        var hitAreaPositionX = options.position.x;
        var hitAreaPositionY = options.position.y;
        var hitAreaWidth = options.cellSize.width * options.reels + options.reelGap * (options.reels - 1); // buggy
        var hitAreaHeight = options.cellSize.height * options.rows;

        this.reelsContainer.hitArea = new PIXI.Rectangle(hitAreaPositionX, hitAreaPositionY, hitAreaWidth, hitAreaHeight);

        this.reelsContainer.x = 0;
        this.reelsContainer.y = 0;

        this._reelsCount = options.reels;
        this._rowsCount = options.rows;
        this._infiniteRotate = false;
        this._isRotationStarted = false;

        this.reels = [];

        for (var i = 0; i < this._reelsCount; ++i) {

            var reel = new Reel (this.reelsContainer, i, options, symbolsCount);
            reel.init();
            reel.setPosition(options.position.x + i * (options.cellSize.width + options.reelGap), options.position.y);

            reel.setBouncingValues([-52, -25, -5, 3, 10, 15, 25, 27, 25, 20, 15, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0]);

            if (i > 0) {
                reel.setPreviousReel(this.reels[i - 1]);
            }

            this.reels.push(reel);
        }
    };

    Barrel.prototype.constructor = Barrel;

    Object.defineProperties(Barrel.prototype, {
        /**
         * PIXI container of barrel reels to be added to scene graph
         * */
        container: {
            get: function () {
                return this.reelsContainer;
            }
        }
    });

    /**
     * Makes barrel visible (if it was previously hidden).
     * @memberOf Barrel
     * */
    Barrel.prototype.show = function () {
        this.reelsContainer.visible = true;
    };

    /**
     * Makes barrel hidden.
     * @memberOf Barrel
     * */
    Barrel.prototype.hide = function () {
        this.reelsContainer.visible = false;
    };

    /**
     * Sets current symbols to show on barrel.
     * @param {Number[]} symbols Array of symbol codes (reel by reel from top of reel to bottom)
     * @memberOf Barrel
     * */
    Barrel.prototype.setCurrentElements = function(symbols) {
        for (var i = 0; i < this._reelsCount; ++i)
        {
            var reelSymbols = symbols.slice(i * this._rowsCount, i * this._rowsCount + this.rows);
            this.reels[i].setSymbols(reelSymbols);
        }
    };

    /**
     * Sets symbols to show on barrel then barrel stops after spin.
     * @param {Number[]} symbols Array of symbol codes (reel by reel from top of reel to bottom)
     * @memberOf Barrel
     * */
    Barrel.prototype.setStopElements = function(symbols) {
        for (var i = 0; i < this._reelsCount; ++i) {
            var reelSymbols = symbols.slice(i * this._rowsCount, i * this._rowsCount + this.rows);
            this.reels[i].setStopElements(reelSymbols);
        }
    };


    /**
     * Sets barrel delegate which responses on barrel events.
     * @param {Object} delegate The barrel's delegate.
     * @memberOf Barrel
     * */
    Barrel.prototype.setDelegate = function(delegate) {
        if (this._delegate !== delegate) {
            this._delegate = delegate;

            for (var i = 0; i < this._reelsCount; ++i) {
                this.reels[i].delegate = delegate;
            }
        }
        else {
            //cc.assert(false, "delegate not responds to BarrelDelegate protocol");
        }
    };


    /**
     * Sets barrel rotation mode. Barrel didn't stops on timeout till this parameter is set to false.
     * @param {Boolean} infiniteRotate true - unable to interrupt, false - enable to interrupt
     * @memberOf Barrel
     * */
    Barrel.prototype.setInfiniteRotate = function(infiniteRotate) {
        for (var i = 0; i < this._reelsCount; ++i) {
            this.reels[i].setInfiniteRotate(infiniteRotate);
        }

        this._infiniteRotate = infiniteRotate;
    };


    /**
     * Updates barrel state. Should be in game loop update method.
     * @param {Number} delta Time shift from previous call.
     * @memberOf Barrel
     * */
    Barrel.prototype.update = function(delta) {
        if (delta > 1.0 / 10.0) {
            delta = 1.0 / 10.0;
        }

        for (var i = 0; i < this._reelsCount; ++i) {
            this.reels[i].update(delta);
        }

        if (this._isRotationStarted && this.isRotating() == false)
        {
            this._isRotationStarted = false;
            this._delegate.barrelDidStop();
        }
    };

    /**
     * Makes barrel to begin rotate.
     * @memberOf Barrel
     * */
    Barrel.prototype.startRotate = function(infinite, previousSymbols, newSymbols)
    {

        if (this._isRotationStarted) {
            throw new Error('trying to start already rotating barrel');
        }


        this.setInfiniteRotate( infinite ? true : false );

        this._isRotationStarted = true;

        for (var index = 0; index < this._reelsCount; index++)
        {
            if (this.reels[index]._isFreezed === false)
            //if(this._frozenReels[index] === -1)
            {
                this.reels[index].startRotate();
            }
        }
    };


    /**
     * Returns if barrel rotating or not.
     * @returns {Boolean} Barrel rotates or not
     * @memberOf Barrel
     * */
    Barrel.prototype.isRotating = function ()
    {
        for (var i = 0; i < this._reelsCount; ++i)
        {
            if (this.reels[i].state !== Reel.State.Idle)
            {
                return true;
            }
        }

        return false;
    };

    /**
     * Sets blur speed - maximum speed on rotation phase.
     * @param {Number} pixelsPerSecond Blur speed
     * @memberOf Barrel
     * */
    Barrel.prototype.setBlurSpeed = function (pixelsPerSecond) {
        for (var i = 0; i < this._reelsCount; ++i) {
            this.reels[i].rotatingSpeed = pixelsPerSecond;
        }
    };


    /**
     * Sets blur rotation time.
     * @param {Number} seconds Time of rotating on maximum speed.
     * @memberOf Barrel
     * */
    Barrel.prototype.setBlurRotationTime = function(seconds)
    {
        for (var i = 0; i < this._reelsCount; ++i)
        {
            this.reels[i].rotatingTime = (i+1) * seconds;
        }
    };

    /**
     * Sets blur rotation time for reel.
     * @param {Number} idx Index of reel.
     * @param {Number} seconds Time of rotating on maximum speed.
     * @memberOf Barrel
     * */
    Barrel.prototype.setReelBlurRotationTime = function (idx, seconds) {
        for (var i = idx; i < this._reelsCount; ++i) {
            this.reels[i].rotatingTime = (i + 1) * seconds;
        }
    };


    /**
     * Makes barrel to stop rotation.
     * @memberOf Barrel
     * */
    Barrel.prototype.stopRotate = function()
    {
        if (this._infiniteRotate == true)
        {
            this.setInfiniteRotate(false);
            return;
        }

        if (this._isRotationStarted) {
            // если барабаны еще в состояниии разгона, то ничего не делаем
            // во всех остальных состояних барабан останавливаем с уведомлением делегата
            for (var i = 0; i < this._reelsCount; ++i)
            {
                if (this.reels[i].state == Reel.State.Accelerating)
                {
                    return;
                }
            }
            this._stopRotateReels();
        }
    };

    /**
     * Interrupts barrel rotation. Barrel stops on current symbols immediately
     * None callback will be called
     * @memberOf Barrel
     * */
    Barrel.prototype.interruptRotate = function()
    {
        this._isRotationStarted = false;
        this._infiniteRotate = false;

        for (var i = 0; i < this._reelsCount; ++i) {
            this.reels[i].interruptRotate();
        }
    };

    Barrel.prototype._stopRotateReels = function () {
        //cc.assert(this._infiniteRotate == false, "infiniteRotate must be OFF before stopRotate!");
        if (this._infiniteRotate == true)
        {
            //cc.warn("YOU SHOULD NOT STOP ROTATION WITH INFINITE ROTATE ON");
            return;
        }

        var arrayOffset = [];
        for (var i = 0; i < this._reelsCount; ++i)
        {
            arrayOffset.push(this.reels[i].offset);
        }

        // поиск максимального и установка нового значения всем reel-ам
        var sortedArray = arrayOffset.sort();
        var maxOffset = sortedArray[sortedArray.length - 1];

        for (var i = 0; i < this._reelsCount; ++i)
        {
            if (this.reels[i].state === Reel.State.Rotating) {
                this.reels[i].offset = maxOffset;
            }
        }

        for (var i = 0; i < this._reelsCount; ++i)
        {
            this.reels[i].stopRotate();
        }
    };

    /**
     * Shows symbol on specified barrel position.
     * @param {Number} position Position on the barrel
     * @memberOf Barrel
     * */
    Barrel.prototype.showSymbolWithIndex = function (position) {
        var reelId = (position / this.rows) | 0;
        if (reelId < this.reels.length) {
            var row = (position % this.rows) | 0;
            this.reels[reelId].setVisibleForSymbolWithIndex(true, row);
        }
    };

    /**
     * Hides symbol on specified barrel position.
     * @param {Number} position Position on the barrel
     * @memberOf Barrel
     * */
    Barrel.prototype.hideSymbolWithIndex = function (position) {
        var reelId = (position / this.rows) | 0;
        if (reelId < this.reels.length) {
            var row = (position % this.rows) | 0;
            this.reels[reelId].setVisibleForSymbolWithIndex(false, row);
        }
    };

    Barrel.prototype.freezeReel = function (idx) {
        if (idx >= this.reels.length) return;

        this.reels[idx].freeze();
    };

    Barrel.prototype.unfreezeReel = function (idx) {
        if (idx >= this.reels.length) return;

        this.reels[idx].unfreeze();
    };

    global.Barrel = Barrel;
}(typeof module === 'object' && module.exports || this));



//////////////////
// WEBPACK FOOTER
// ./src/library/barrel.js
// module id = 94
// module chunks = 0